import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  useCreateMaquilaMovement,
  useMaquilaAreas,
  useMaquilaItemInventory,
  useMaquilaItems,
} from "~/hooks/maquila";
import type {
  MaquilaMovementFormData,
  MaquilaMovementType,
} from "~/types/maquila";

// UI movement type includes client_transfer for inter-client transfers
type UIMovementType = MaquilaMovementType | "client_transfer";

export const MaquilaMovementFormPage = () => {
  const navigate = useNavigate();

  const [movementType, setMovementType] = useState<UIMovementType>("entry");
  // Source (for exit, transfer, client_transfer)
  const [clientId, setClientId] = useState<string>("");
  const [itemId, setItemId] = useState<string>("");
  const [fromAreaId, setFromAreaId] = useState<string>("");
  // Destination (for entry, transfer)
  const [toAreaId, setToAreaId] = useState<string>("");
  // Destination client/item (for client_transfer only)
  const [destClientId, setDestClientId] = useState<string>("");
  const [destItemId, setDestItemId] = useState<string>("");
  const [destAreaId, setDestAreaId] = useState<string>("");
  // Common fields
  const [quantity, setQuantity] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [referenceDocument, setReferenceDocument] = useState<string>("");

  const { data: areas } = useMaquilaAreas();
  const { data: items } = useMaquilaItems(clientId || undefined);
  const { data: itemInventory } = useMaquilaItemInventory(itemId);
  // For client_transfer destination
  const { data: destItems } = useMaquilaItems(destClientId || undefined);

  const createMutation = useCreateMaquilaMovement();

  // Get unique clients from items
  const clientsMap = new Map<string, { id: string; name: string }>();
  items?.forEach((item) => {
    if (item.client) {
      clientsMap.set(item.client.id, item.client);
    }
  });
  const { data: allItems } = useMaquilaItems();
  allItems?.forEach((item) => {
    if (item.client) {
      clientsMap.set(item.client.id, item.client);
    }
  });
  const clients = Array.from(clientsMap.values());

  // Filter items by selected client
  const filteredItems = clientId
    ? items?.filter((i) => i.client_id === clientId)
    : [];

  // Get available stock in source area for exit/transfer
  const getAvailableStock = () => {
    if (!fromAreaId || !itemInventory) return 0;
    const inv = itemInventory.find((i) => i.maquila_area_id === fromAreaId);
    return inv ? Number(inv.quantity) : 0;
  };

  const selectedItem = items?.find((i) => i.id === itemId);

  // Reset dependent fields when movement type changes
  useEffect(() => {
    setFromAreaId("");
    setToAreaId("");
    setDestClientId("");
    setDestItemId("");
    setDestAreaId("");
  }, [movementType]);

  // Reset item when client changes
  useEffect(() => {
    setItemId("");
    setFromAreaId("");
    setToAreaId("");
  }, [clientId]);

  // Reset destination item when destination client changes
  useEffect(() => {
    setDestItemId("");
    setDestAreaId("");
  }, [destClientId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const qty = parseFloat(quantity);
    const refDoc = referenceDocument || undefined;
    const notesText = notes || undefined;

    try {
      if (movementType === "client_transfer") {
        // Inter-client transfer creates TWO movements:
        // 1. Exit from source client's item
        const exitData: MaquilaMovementFormData = {
          movement_type: "exit",
          maquila_item_id: itemId,
          from_area_id: fromAreaId,
          quantity: qty,
          notes: notesText
            ? `[Transferencia a ${destItems?.find((i) => i.id === destItemId)?.client?.name}] ${notesText}`
            : `Transferencia a ${destItems?.find((i) => i.id === destItemId)?.client?.name}`,
          reference_document: refDoc,
        };
        // 2. Entry to destination client's item
        const entryData: MaquilaMovementFormData = {
          movement_type: "entry",
          maquila_item_id: destItemId,
          to_area_id: destAreaId,
          quantity: qty,
          notes: notesText
            ? `[Transferencia desde ${items?.find((i) => i.id === itemId)?.client?.name}] ${notesText}`
            : `Transferencia desde ${items?.find((i) => i.id === itemId)?.client?.name}`,
          reference_document: refDoc,
        };

        await createMutation.mutateAsync(exitData);
        await createMutation.mutateAsync(entryData);
      } else {
        // Standard movement (entry, exit, transfer)
        const formData: MaquilaMovementFormData = {
          movement_type: movementType as MaquilaMovementType,
          maquila_item_id: itemId,
          quantity: qty,
          notes: notesText,
          reference_document: refDoc,
        };

        if (movementType === "entry") {
          formData.to_area_id = toAreaId;
        } else if (movementType === "exit") {
          formData.from_area_id = fromAreaId;
        }

        await createMutation.mutateAsync(formData);
      }
      navigate("/maquila/movements");
    } catch (error) {
      // Error handled by mutation
    }
  };

  const isValid = () => {
    if (!quantity || parseFloat(quantity) <= 0) return false;

    if (movementType === "client_transfer") {
      // Validate source
      if (!clientId || !itemId || !fromAreaId) return false;
      // Validate destination
      if (!destClientId || !destItemId || !destAreaId) return false;
      // Source and destination clients must be different
      if (clientId === destClientId) return false;
      // Check source stock
      const available = getAvailableStock();
      if (parseFloat(quantity) > available) return false;
      return true;
    }

    // Standard movements
    if (!itemId) return false;

    if (movementType === "entry" && !toAreaId) return false;
    if (movementType === "exit" && !fromAreaId) return false;

    if (movementType !== "entry") {
      const available = getAvailableStock();
      if (parseFloat(quantity) > available) return false;
    }

    return true;
  };

  return (
    <div className="mx-auto w-full p-6 lg:w-[80vw]">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/maquila")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Registrar Movimiento
          </h1>
          <p className="text-sm text-muted-foreground">
            Entrada, salida o transferencia de materiales
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Datos del Movimiento</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Movement Type */}
            <div className="space-y-2">
              <Label>Tipo de Movimiento *</Label>
              <Select
                value={movementType}
                onValueChange={(v) => setMovementType(v as UIMovementType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entry">
                    Entrada - Cliente envia materiales
                  </SelectItem>
                  <SelectItem value="exit">
                    Salida - Devolver materiales al cliente
                  </SelectItem>
                  <SelectItem value="client_transfer">
                    Transferencia - Entre clientes
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Source Client Section */}
            {movementType === "client_transfer" && (
              <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-orange-800">
                  <ArrowRight className="h-4 w-4 rotate-180" />
                  Cliente Origen
                </h3>
                <div className="space-y-4">
                  {/* Source Client */}
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <Select value={clientId} onValueChange={setClientId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar cliente origen" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Source Item */}
                  {clientId && (
                    <div className="space-y-2">
                      <Label>Material *</Label>
                      <Select value={itemId} onValueChange={setItemId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar material" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredItems?.map((item) => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.name}
                              {item.code && ` (${item.code})`} - {item.unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Source Area */}
                  {itemId && (
                    <div className="space-y-2">
                      <Label>Area Origen *</Label>
                      <Select value={fromAreaId} onValueChange={setFromAreaId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar area" />
                        </SelectTrigger>
                        <SelectContent>
                          {areas?.map((area) => {
                            const inv = itemInventory?.find(
                              (i) => i.maquila_area_id === area.id
                            );
                            const stock = inv ? Number(inv.quantity) : 0;
                            return (
                              <SelectItem
                                key={area.id}
                                value={area.id}
                                disabled={stock <= 0}
                              >
                                {area.name} (Stock:{" "}
                                {stock.toLocaleString("es-CL")}{" "}
                                {selectedItem?.unit})
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {fromAreaId && (
                        <p className="text-xs text-muted-foreground">
                          Disponible:{" "}
                          {getAvailableStock().toLocaleString("es-CL")}{" "}
                          {selectedItem?.unit}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Destination Client Section (for client_transfer) */}
            {movementType === "client_transfer" && (
              <div className="rounded-lg border border-green-200 bg-green-50/50 p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-green-800">
                  <ArrowRight className="h-4 w-4" />
                  Cliente Destino
                </h3>
                <div className="space-y-4">
                  {/* Destination Client */}
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <Select value={destClientId} onValueChange={setDestClientId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar cliente destino" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients
                          .filter((c) => c.id !== clientId)
                          .map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {clientId && clients.filter((c) => c.id !== clientId).length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No hay otros clientes disponibles para transferir.
                      </p>
                    )}
                  </div>

                  {/* Destination Item */}
                  {destClientId && (
                    <div className="space-y-2">
                      <Label>Material *</Label>
                      <Select value={destItemId} onValueChange={setDestItemId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar material destino" />
                        </SelectTrigger>
                        <SelectContent>
                          {destItems
                            ?.filter((i) => i.client_id === destClientId)
                            .map((item) => (
                              <SelectItem key={item.id} value={item.id}>
                                {item.name}
                                {item.code && ` (${item.code})`} - {item.unit}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {destItems?.filter((i) => i.client_id === destClientId).length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          Este cliente no tiene materiales registrados.{" "}
                          <Button
                            variant="link"
                            className="h-auto p-0 text-xs"
                            onClick={() => navigate("/maquila/items")}
                          >
                            Crear material
                          </Button>
                        </p>
                      )}
                    </div>
                  )}

                  {/* Destination Area */}
                  {destItemId && (
                    <div className="space-y-2">
                      <Label>Area Destino *</Label>
                      <Select value={destAreaId} onValueChange={setDestAreaId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar area" />
                        </SelectTrigger>
                        <SelectContent>
                          {areas?.map((area) => (
                            <SelectItem key={area.id} value={area.id}>
                              {area.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Standard Client (for non-client_transfer) */}
            {movementType !== "client_transfer" && (
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {clients.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Primero debe crear materiales para clientes en la seccion
                    &quot;Materiales&quot;
                  </p>
                )}
              </div>
            )}

            {/* Standard Item (for non-client_transfer) */}
            {movementType !== "client_transfer" && clientId && (
              <div className="space-y-2">
                <Label>Material *</Label>
                <Select value={itemId} onValueChange={setItemId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar material" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredItems?.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                        {item.code && ` (${item.code})`} - {item.unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filteredItems?.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Este cliente no tiene materiales registrados.{" "}
                    <Button
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => navigate("/maquila/items")}
                    >
                      Crear material
                    </Button>
                  </p>
                )}
              </div>
            )}

            {/* Areas (for entry/exit only, not client_transfer) */}
            {movementType !== "client_transfer" && itemId && (
              <div className="space-y-2">
                {/* From Area (exit) */}
                {movementType === "exit" && (
                  <>
                    <Label>Area Origen *</Label>
                    <Select value={fromAreaId} onValueChange={setFromAreaId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar area" />
                      </SelectTrigger>
                      <SelectContent>
                        {areas?.map((area) => {
                          const inv = itemInventory?.find(
                            (i) => i.maquila_area_id === area.id
                          );
                          const stock = inv ? Number(inv.quantity) : 0;
                          return (
                            <SelectItem
                              key={area.id}
                              value={area.id}
                              disabled={stock <= 0}
                            >
                              {area.name} (Stock:{" "}
                              {stock.toLocaleString("es-CL")}{" "}
                              {selectedItem?.unit})
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {fromAreaId && (
                      <p className="text-xs text-muted-foreground">
                        Disponible:{" "}
                        {getAvailableStock().toLocaleString("es-CL")}{" "}
                        {selectedItem?.unit}
                      </p>
                    )}
                  </>
                )}

                {/* To Area (entry) */}
                {movementType === "entry" && (
                  <>
                    <Label>Area Destino *</Label>
                    <Select value={toAreaId} onValueChange={setToAreaId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar area" />
                      </SelectTrigger>
                      <SelectContent>
                        {areas?.map((area) => (
                          <SelectItem key={area.id} value={area.id}>
                            {area.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
            )}

            {/* Quantity */}
            {(movementType === "client_transfer" ? (itemId && destItemId) : itemId) && (
              <div className="space-y-2">
                <Label>
                  Cantidad ({movementType === "client_transfer" ? selectedItem?.unit : selectedItem?.unit}) *
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder={`Cantidad en ${selectedItem?.unit}`}
                />
                {((movementType !== "entry" && movementType !== "client_transfer") ||
                  movementType === "client_transfer") &&
                  fromAreaId &&
                  parseFloat(quantity) > getAvailableStock() && (
                    <p className="text-xs text-destructive">
                      La cantidad supera el stock disponible en origen (
                      {getAvailableStock().toLocaleString("es-CL")} {selectedItem?.unit})
                    </p>
                  )}
              </div>
            )}

            {/* Reference Document */}
            <div className="space-y-2">
              <Label>Documento de Referencia</Label>
              <Input
                value={referenceDocument}
                onChange={(e) => setReferenceDocument(e.target.value)}
                placeholder="Ej: Guia #1234, OC #5678"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observaciones adicionales..."
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/maquila")}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!isValid() || createMutation.isPending}
              >
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Registrar Movimiento
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
